// src/components/AboutUs.jsx
import React from "react";
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Chip,
  Stack,
  Button,
} from "@mui/material";
import { useSEO } from "./SEO"; // أبقيته من دون تعديل

// Icons
import ShieldIcon from "@mui/icons-material/Shield";
import QueryStatsIcon from "@mui/icons-material/QueryStats";
import HeadsetMicIcon from "@mui/icons-material/HeadsetMic";
import LockPersonIcon from "@mui/icons-material/LockPerson";
import VerifiedUserIcon from "@mui/icons-material/VerifiedUser";
import TimelineIcon from "@mui/icons-material/Timeline";
import SpeedIcon from "@mui/icons-material/Speed";
import CandlestickChartIcon from "@mui/icons-material/CandlestickChart";
import InsightsIcon from "@mui/icons-material/Insights";

const BINANCE_YELLOW = "#F0B90B";
const BG = "#263238";
const SURFACE = "#37474F";
const BORDER = "rgba(255,255,255,0.08)";
const TEXT = "#EAEFF2";
const MUTED = "#B0BEC5";

export default function AboutUs() {
  useSEO?.({
    title: "About 1CryptoX — Secure Crypto Exchange | Mission, Vision, Team",
    description:
      "1CryptoX aims to redefine the digital asset trading experience with security-first engineering, intuitive UX, and professional analytics — built for both retail and institutional users.",
    keywords: [
      "about us",
      "crypto exchange team",
      "mission vision",
      "security first",
      "institutional grade",
      "low fees",
      "trading platform",
    ],
    canonical: "https://www.1cryptox.com/about-us",
  });

  return (
    <Box
      sx={{
        minHeight: "100vh",
        width: "100%",
        py: { xs: 4, md: 6 },
        px: { xs: 2, md: 4 },
        position: "relative",
        overflow: "hidden",
        // ⬇️ نفس الخلفية العامة من صفحة Welcome
        background:
          "radial-gradient(60% 60% at 70% 30%, rgba(240,185,11,0.10) 0%, rgba(240,185,11,0.03) 100%)",
        bgcolor: "#0B0E11",
      }}
    >
      {/* محتوى الصفحة */}
      <Box sx={{ mt: 3 }}>
        {/* Hero */}
        <Box
          sx={{
            bgcolor: BG,
            border: `1px solid ${BORDER}`,
            borderRadius: 2,
            px: { xs: 2, md: 3 },
            py: { xs: 3, md: 4 },
            boxShadow: "0 8px 24px rgba(0,0,0,0.35)",
            overflow: "hidden",
            position: "relative",
          }}
        >
          <Container maxWidth="lg" disableGutters>
            <Typography
              variant="h3"
              sx={{ fontWeight: 900, color: TEXT, mb: 1, letterSpacing: 0.2 }}
            >
              About 1CryptoX
            </Typography>
            <Typography sx={{ color: MUTED, mb: 2 }}>
              We are building a security-first, analytics-driven crypto platform
              that blends institutional-grade infrastructure with an intuitive,
              modern trading experience for everyone.
            </Typography>

            <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap" }}>
              <Chip
                icon={<ShieldIcon sx={{ color: BINANCE_YELLOW }} />}
                label="Security-first"
                sx={{
                  bgcolor: "rgba(240,185,11,0.10)",
                  color: BINANCE_YELLOW,
                  border: `1px solid rgba(240,185,11,0.25)`,
                  fontWeight: 700,
                }}
              />
              <Chip
                icon={<QueryStatsIcon sx={{ color: BINANCE_YELLOW }} />}
                label="Pro Analytics"
                variant="outlined"
                sx={{ color: TEXT, borderColor: BORDER, fontWeight: 700 }}
              />
              <Chip
                icon={<HeadsetMicIcon sx={{ color: BINANCE_YELLOW }} />}
                label="24/7 Support"
                variant="outlined"
                sx={{ color: TEXT, borderColor: BORDER, fontWeight: 700 }}
              />
            </Stack>
          </Container>
        </Box>

        {/* Vision / Mission / Values */}
        <Box sx={{ mt: 3 }}>
          <Container maxWidth="lg" disableGutters>
            <Grid container spacing={2}>
              {[
                {
                  title: "Vision",
                  copy:
                    "To set the global benchmark for secure, intelligent, and accessible digital-asset trading.",
                  icon: <InsightsIcon />,
                },
                {
                  title: "Mission",
                  copy:
                    "Deliver advanced Spot & Perpetual trading, transparent fees, and reliable infrastructure with an elegant, intuitive UX.",
                  icon: <TimelineIcon />,
                },
                {
                  title: "Values",
                  copy:
                    "Transparency, security, innovation, and deep partnerships with leading technology providers.",
                  icon: <VerifiedUserIcon />,
                },
              ].map((c, i) => (
                <Grid item xs={12} md={4} key={i}>
                  <Card
                    sx={{
                      height: "100%",
                      bgcolor: BG,
                      border: `1px solid ${BORDER}`,
                      borderRadius: 2,
                      boxShadow: "0 8px 24px rgba(0,0,0,0.35)",
                    }}
                  >
                    <CardContent sx={{ p: { xs: 2, md: 2.5 } }}>
                      <Stack direction="row" spacing={1.25} alignItems="center">
                        <Box
                          sx={{
                            width: 46,
                            height: 46,
                            bgcolor: SURFACE,
                            borderRadius: "999px",
                            border: `1px solid ${BORDER}`,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            boxShadow: "inset 0 0 0 1px rgba(255,255,255,0.06)",
                          }}
                        >
                          <Box
                            sx={{
                              color: BINANCE_YELLOW,
                              "& svg": { fontSize: 24 },
                            }}
                          >
                            {c.icon}
                          </Box>
                        </Box>
                        <Typography
                          variant="h6"
                          sx={{ fontWeight: 800, color: TEXT }}
                        >
                          {c.title}
                        </Typography>
                      </Stack>
                      <Typography sx={{ mt: 1, color: MUTED }}>
                        {c.copy}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Container>
        </Box>

        {/* Pillars */}
        <Box sx={{ mt: 3 }}>
          <Container maxWidth="lg" disableGutters>
            <Grid container spacing={2}>
              {[
                {
                  title: "Security & Compliance",
                  copy:
                    "Multi-layer protection (encryption, 2FA), withdrawal allow-lists, and best-practice AML/KYC frameworks.",
                  icon: <LockPersonIcon />,
                  color: "#42A5F5",
                },
                {
                  title: "Performance & Reliability",
                  copy:
                    "Low-latency matching, scalable services, and real-time data pipelines designed for uptime.",
                  icon: <SpeedIcon />,
                  color: "#26A69A",
                },
                {
                  title: "User Experience",
                  copy:
                    "A clean, consistent UI that prioritizes clarity, speed, and discoverability across devices.",
                  icon: <QueryStatsIcon />,
                  color: "#AB47BC",
                },
                {
                  title: "Liquidity & Markets",
                  copy:
                    "Deep markets, institutional integrations, and pro-grade tools for price discovery and execution.",
                  icon: <CandlestickChartIcon />,
                  color: "#FFC107",
                },
              ].map((p, i) => (
                <Grid item xs={12} md={6} key={i}>
                  <Card
                    sx={{
                      height: "100%",
                      bgcolor: BG,
                      border: `1px solid ${BORDER}`,
                      borderRadius: 2,
                    }}
                  >
                    <CardContent sx={{ p: { xs: 2, md: 2.5 } }}>
                      <Stack
                        direction="row"
                        spacing={1.25}
                        alignItems="flex-start"
                      >
                        <Box
                          sx={{
                            width: 46,
                            height: 46,
                            bgcolor: SURFACE,
                            borderRadius: "999px",
                            border: `1px solid ${BORDER}`,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            position: "relative",
                            overflow: "hidden",
                            boxShadow: `0 0 0 2px rgba(240,185,11,0.15)`,
                            "&::after": {
                              content: '""',
                              position: "absolute",
                              inset: 0,
                              background:
                                "linear-gradient(135deg, rgba(240,185,11,0.22), transparent 45%)",
                            },
                          }}
                        >
                          <Box
                            sx={{ color: p.color, "& svg": { fontSize: 24 } }}
                          >
                            {p.icon}
                          </Box>
                        </Box>
                        <Box>
                          <Typography sx={{ fontWeight: 800, color: TEXT }}>
                            {p.title}
                          </Typography>
                          <Typography sx={{ color: MUTED, mt: 0.5 }}>
                            {p.copy}
                          </Typography>
                        </Box>
                      </Stack>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Container>
        </Box>

        {/* Numbers / KPIs (placeholders) */}
        <Box sx={{ mt: 3 }}>
          <Container maxWidth="lg" disableGutters>
            <Card
              sx={{
                bgcolor: BG,
                border: `1px solid ${BORDER}`,
                borderRadius: 2,
                p: { xs: 2, md: 3 },
              }}
            >
              <Grid container spacing={2}>
                {[
                  { label: "Avg. order latency", value: "< 25 ms" },
                  { label: "Uptime target", value: "99.95%" },
                  { label: "Supported pairs", value: "100+ (roadmap)" },
                  { label: "Support SLA", value: "< 2 min (chat)" },
                ].map((k, i) => (
                  <Grid item xs={6} md={3} key={i}>
                    <Stack spacing={0.5}>
                      <Typography
                        sx={{
                          color: BINANCE_YELLOW,
                          fontWeight: 900,
                          fontSize: 20,
                        }}
                      >
                        {k.value}
                      </Typography>
                      <Typography sx={{ color: MUTED, fontSize: 13 }}>
                        {k.label}
                      </Typography>
                    </Stack>
                  </Grid>
                ))}
              </Grid>

              {/* CTA */}
              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={1.25}
                sx={{ mt: 2 }}
              >
                <Button
                  href="/register"
                  variant="contained"
                  sx={{
                    bgcolor: BINANCE_YELLOW,
                    color: "#1C2526",
                    fontWeight: 800,
                    textTransform: "none",
                    "&:hover": { bgcolor: "#FFD54F" },
                  }}
                >
                  Create an account
                </Button>
                <Button
                  href="/markets"
                  variant="outlined"
                  sx={{
                    color: TEXT,
                    borderColor: BORDER,
                    textTransform: "none",
                    fontWeight: 800,
                    "&:hover": { borderColor: "rgba(255,255,255,0.35)" },
                  }}
                >
                  Explore markets
                </Button>
              </Stack>
            </Card>
          </Container>
        </Box>
      </Box>
    </Box>
  );
}
